import { useState, useEffect, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Container } from '@beyond-silence/ui';
import { supabase } from '@/utils/supabase';

interface TeamMember {
  id: string;
  display_name: string | null;
  avatar_url: string | null;
  bio: string | null;
  title: string | null;
  public_slug: string | null;
  is_public: boolean;
  role: 'reader' | 'author' | 'editor' | 'admin';
  email: string;
  created_at: string;
  updated_at: string;
}

const ROLE_OPTIONS = ['reader', 'author', 'editor', 'admin'] as const;

export function TeamPage() {
  const { t } = useTranslation('staff');
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Track inline edits: memberId -> { field: value }
  const [edits, setEdits] = useState<Record<string, Partial<TeamMember>>>({});
  const [editingId, setEditingId] = useState<string | null>(null);

  // Add member modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ email: '', password: '', display_name: '', title: '', role: 'author' as TeamMember['role'] });
  const [adding, setAdding] = useState(false);

  const fetchTeam = async () => {
    setLoading(true);
    setError(null);
    const { data, error: rpcErr } = await supabase.rpc('get_team_members');
    if (rpcErr) {
      setError(rpcErr.message);
    } else {
      setMembers((data as TeamMember[]) ?? []);
    }
    setLoading(false);
  };

  useEffect(() => { fetchTeam(); }, []);

  const startEdit = (member: TeamMember) => {
    setEditingId(member.id);
    setEdits(prev => ({
      ...prev,
      [member.id]: {
        display_name: member.display_name ?? '',
        title: member.title ?? '',
        bio: member.bio ?? '',
        public_slug: member.public_slug ?? '',
        is_public: member.is_public,
        role: member.role,
        avatar_url: member.avatar_url ?? '',
      },
    }));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEdits(prev => {
      const next = { ...prev };
      if (editingId) delete next[editingId];
      return next;
    });
  };

  const updateEdit = (id: string, field: string, value: string | boolean) => {
    setEdits(prev => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  const saveEdit = async (memberId: string) => {
    const patch = edits[memberId];
    if (!patch) return;
    setSaving(memberId);
    setMessage(null);

    const updateData: Record<string, unknown> = {};
    if (patch.display_name !== undefined) updateData.display_name = patch.display_name || null;
    if (patch.title !== undefined) updateData.title = patch.title || null;
    if (patch.bio !== undefined) updateData.bio = patch.bio || null;
    if (patch.public_slug !== undefined) updateData.public_slug = patch.public_slug || null;
    if (patch.is_public !== undefined) updateData.is_public = patch.is_public;
    if (patch.role !== undefined) updateData.role = patch.role;
    if (patch.avatar_url !== undefined) updateData.avatar_url = patch.avatar_url || null;

    const { error: updateErr } = await (supabase
      .from('profiles') as any)
      .update(updateData)
      .eq('id', memberId);

    if (updateErr) {
      setMessage({ type: 'error', text: updateErr.message });
    } else {
      setMessage({ type: 'success', text: 'Profile updated.' });
      setEditingId(null);
      setEdits(prev => { const n = { ...prev }; delete n[memberId]; return n; });
      fetchTeam(); // refresh
    }
    setSaving(null);
  };

  const sendPasswordReset = async (email: string) => {
    setMessage(null);
    const { error: resetErr } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/login`,
    });
    if (resetErr) {
      setMessage({ type: 'error', text: resetErr.message });
    } else {
      setMessage({ type: 'success', text: `Password reset email sent to ${email}` });
    }
  };

  const handleAddMember = async (e: FormEvent) => {
    e.preventDefault();
    if (!addForm.email || !addForm.password) return;
    setAdding(true);
    setMessage(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('Not authenticated');

      const resp = await fetch('https://api.beyondsilence.fr/admin/create-user', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(addForm),
      });

      const result = await resp.json();
      if (!resp.ok) {
        throw new Error(result.error || 'Failed to create user');
      }

      setMessage({ type: 'success', text: `Created ${result.user.email} as ${result.user.role}` });
      setShowAddModal(false);
      setAddForm({ email: '', password: '', display_name: '', title: '', role: 'author' });
      fetchTeam();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message });
    }
    setAdding(false);
  };

  const roleBadge = (role: string) => {
    const map: Record<string, string> = {
      admin: 'bg-primary text-white',
      editor: 'bg-highlight/60 text-ink',
      author: 'bg-soft/60 text-ink',
      reader: 'bg-gray-200 text-gray-600',
    };
    return map[role] ?? 'bg-gray-200 text-gray-600';
  };

  const avatarFallback = (name: string | null) =>
    name ? name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : '?';

  if (loading) {
    return (
      <Container size="dashboard" className="flex min-h-[40vh] items-center justify-center py-8">
        <div className="font-ui text-ui-base text-ink-light">Loading…</div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container size="dashboard" className="py-4 sm:py-6 md:py-8">
        <div className="rounded-lg border border-primary/30 bg-primary/5 p-6 text-center">
          <p className="font-ui text-ui-sm text-primary">{error}</p>
        </div>
      </Container>
    );
  }

  return (
    <Container size="dashboard" className="py-4 sm:py-6 md:py-8">
      <div className="mb-4 flex items-center justify-between sm:mb-6">
        <h1 className="font-ui text-ui-lg font-bold text-ink">Team</h1>
        <div className="flex items-center gap-3">
          <span className="font-ui text-ui-sm text-ink-light">{members.length} member{members.length !== 1 ? 's' : ''}</span>
          <button
            onClick={() => setShowAddModal(true)}
            className="rounded-md bg-primary px-3 py-1.5 font-ui text-ui-sm font-medium text-white hover:bg-primary/90"
          >
            + Add Member
          </button>
        </div>
      </div>

      {message && (
        <div className={`mb-4 rounded-md px-4 py-2 font-ui text-ui-sm ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-primary/10 text-primary'}`}>
          {message.text}
        </div>
      )}

      {/* Desktop table */}
      <div className="hidden overflow-hidden rounded-lg border border-soft/40 bg-white md:block">
        <table className="w-full">
          <thead>
            <tr className="border-b border-soft/40 bg-soft/20">
              <th className="px-4 py-3 text-left font-ui text-ui-xs font-semibold text-ink-light">Member</th>
              <th className="px-4 py-3 text-left font-ui text-ui-xs font-semibold text-ink-light">Email</th>
              <th className="px-4 py-3 text-left font-ui text-ui-xs font-semibold text-ink-light">Title</th>
              <th className="px-4 py-3 text-left font-ui text-ui-xs font-semibold text-ink-light">Role</th>
              <th className="px-4 py-3 text-right font-ui text-ui-xs font-semibold text-ink-light">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-soft/30">
            {members.map((m) => (
              <tr key={m.id} className={editingId === m.id ? 'bg-highlight/10' : 'hover:bg-soft/10'}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    {m.avatar_url ? (
                      <img src={m.avatar_url} alt="" className="h-8 w-8 rounded-full object-cover ring-1 ring-soft/40" />
                    ) : (
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary/20 font-ui text-ui-xs font-bold text-secondary">
                        {avatarFallback(m.display_name)}
                      </div>
                    )}
                    <span className="font-ui text-ui-sm font-medium text-ink">{m.display_name ?? '—'}</span>
                  </div>
                </td>
                <td className="px-4 py-3 font-ui text-ui-sm text-ink-light">{m.email}</td>
                <td className="px-4 py-3 font-ui text-ui-sm text-ink-light">{m.title ?? '—'}</td>
                <td className="px-4 py-3">
                  {editingId === m.id ? (
                    <select
                      value={edits[m.id]?.role ?? m.role}
                      onChange={(e) => updateEdit(m.id, 'role', e.target.value)}
                      className="rounded-md border border-soft/60 bg-white px-2 py-1 font-ui text-ui-sm text-ink"
                    >
                      {ROLE_OPTIONS.map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  ) : (
                    <span className={`inline-block rounded-pill px-2 py-0.5 font-ui text-ui-xs font-medium ${roleBadge(m.role)}`}>
                      {m.role}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  {editingId === m.id ? (
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => saveEdit(m.id)}
                        disabled={saving === m.id}
                        className="rounded-md bg-primary px-3 py-1.5 font-ui text-ui-xs font-medium text-white hover:bg-primary/90 disabled:opacity-50"
                      >
                        {saving === m.id ? 'Saving…' : 'Save'}
                      </button>
                      <button
                        onClick={cancelEdit}
                        className="rounded-md bg-soft/40 px-3 py-1.5 font-ui text-ui-xs font-medium text-ink-light hover:bg-soft/60"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => startEdit(m)}
                      className="rounded-md px-3 py-1.5 font-ui text-ui-xs font-medium text-secondary hover:bg-secondary/10"
                    >
                      Edit
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="space-y-3 md:hidden">
        {members.map((m) => (
          <div key={m.id} className={`rounded-lg border border-soft/40 bg-white p-4 ${editingId === m.id ? 'ring-2 ring-highlight' : ''}`}>
            <div className="flex items-center gap-3">
              {m.avatar_url ? (
                <img src={m.avatar_url} alt="" className="h-10 w-10 rounded-full object-cover ring-1 ring-soft/40" />
              ) : (
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary/20 font-ui text-ui-sm font-bold text-secondary">
                  {avatarFallback(m.display_name)}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate font-ui text-ui-sm font-semibold text-ink">{m.display_name ?? '—'}</p>
                <p className="truncate font-ui text-ui-xs text-ink-light">{m.email}</p>
              </div>
              {editingId !== m.id && (
                <span className={`inline-block shrink-0 rounded-pill px-2 py-0.5 font-ui text-ui-xs font-medium ${roleBadge(m.role)}`}>
                  {m.role}
                </span>
              )}
            </div>

            {editingId === m.id ? (
              <div className="mt-3 space-y-3 border-t border-soft/30 pt-3">
                <label className="block">
                  <span className="font-ui text-ui-xs text-ink-light">Display Name</span>
                  <input
                    type="text"
                    value={edits[m.id]?.display_name ?? ''}
                    onChange={(e) => updateEdit(m.id, 'display_name', e.target.value)}
                    className="mt-1 w-full rounded-md border border-soft/60 px-3 py-1.5 font-ui text-ui-sm text-ink"
                  />
                </label>
                <label className="block">
                  <span className="font-ui text-ui-xs text-ink-light">Title</span>
                  <input
                    type="text"
                    value={edits[m.id]?.title ?? ''}
                    onChange={(e) => updateEdit(m.id, 'title', e.target.value)}
                    className="mt-1 w-full rounded-md border border-soft/60 px-3 py-1.5 font-ui text-ui-sm text-ink"
                  />
                </label>
                <label className="block">
                  <span className="font-ui text-ui-xs text-ink-light">Role</span>
                  <select
                    value={edits[m.id]?.role ?? m.role}
                    onChange={(e) => updateEdit(m.id, 'role', e.target.value)}
                    className="mt-1 w-full rounded-md border border-soft/60 bg-white px-3 py-1.5 font-ui text-ui-sm text-ink"
                  >
                    {ROLE_OPTIONS.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </label>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => saveEdit(m.id)}
                    disabled={saving === m.id}
                    className="rounded-md bg-primary px-3 py-1.5 font-ui text-ui-xs font-medium text-white hover:bg-primary/90 disabled:opacity-50"
                  >
                    {saving === m.id ? 'Saving…' : 'Save'}
                  </button>
                  <button onClick={cancelEdit} className="rounded-md bg-soft/40 px-3 py-1.5 font-ui text-ui-xs font-medium text-ink-light hover:bg-soft/60">
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="mt-2 flex items-center justify-between">
                <span className="font-ui text-ui-xs text-ink-light">{m.title ?? 'No title'}</span>
                <button
                  onClick={() => startEdit(m)}
                  className="rounded-md px-3 py-1.5 font-ui text-ui-xs font-medium text-secondary hover:bg-secondary/10"
                >
                  Edit
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* ── Edit modal (desktop) ── */}
      {editingId && (
        <div className="hidden md:block">
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40" onClick={cancelEdit}>
            <div
              className="mx-4 w-full max-w-lg rounded-xl bg-white p-6 shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="font-ui text-ui-base font-bold text-ink">
                Edit — {members.find(m => m.id === editingId)?.display_name ?? 'Member'}
              </h2>

              <div className="mt-4 space-y-4">
                {/* Avatar URL */}
                <label className="block">
                  <span className="font-ui text-ui-xs text-ink-light">Avatar URL</span>
                  <input
                    type="text"
                    value={edits[editingId]?.avatar_url ?? ''}
                    onChange={(e) => updateEdit(editingId, 'avatar_url', e.target.value)}
                    className="mt-1 w-full rounded-md border border-soft/60 px-3 py-1.5 font-ui text-ui-sm text-ink"
                    placeholder="https://example.com/avatar.jpg"
                  />
                </label>

                <div className="grid grid-cols-2 gap-4">
                  <label className="block">
                    <span className="font-ui text-ui-xs text-ink-light">Display Name</span>
                    <input
                      type="text"
                      value={edits[editingId]?.display_name ?? ''}
                      onChange={(e) => updateEdit(editingId, 'display_name', e.target.value)}
                      className="mt-1 w-full rounded-md border border-soft/60 px-3 py-1.5 font-ui text-ui-sm text-ink"
                    />
                  </label>
                  <label className="block">
                    <span className="font-ui text-ui-xs text-ink-light">Title</span>
                    <input
                      type="text"
                      value={edits[editingId]?.title ?? ''}
                      onChange={(e) => updateEdit(editingId, 'title', e.target.value)}
                      className="mt-1 w-full rounded-md border border-soft/60 px-3 py-1.5 font-ui text-ui-sm text-ink"
                      placeholder="e.g. Senior Correspondent"
                    />
                  </label>
                </div>

                <label className="block">
                  <span className="font-ui text-ui-xs text-ink-light">Bio</span>
                  <textarea
                    rows={3}
                    value={edits[editingId]?.bio ?? ''}
                    onChange={(e) => updateEdit(editingId, 'bio', e.target.value)}
                    className="mt-1 w-full rounded-md border border-soft/60 px-3 py-1.5 font-ui text-ui-sm text-ink"
                  />
                </label>

                <div className="grid grid-cols-2 gap-4">
                  <label className="block">
                    <span className="font-ui text-ui-xs text-ink-light">Public URL Slug</span>
                    <input
                      type="text"
                      value={edits[editingId]?.public_slug ?? ''}
                      onChange={(e) => updateEdit(editingId, 'public_slug', e.target.value)}
                      className="mt-1 w-full rounded-md border border-soft/60 px-3 py-1.5 font-ui text-ui-sm text-ink"
                    />
                  </label>
                  <label className="block">
                    <span className="font-ui text-ui-xs text-ink-light">Role</span>
                    <select
                      value={edits[editingId]?.role ?? members.find(m => m.id === editingId)?.role ?? 'reader'}
                      onChange={(e) => updateEdit(editingId, 'role', e.target.value)}
                      className="mt-1 w-full rounded-md border border-soft/60 bg-white px-3 py-1.5 font-ui text-ui-sm text-ink"
                    >
                      {ROLE_OPTIONS.map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </label>
                </div>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={edits[editingId]?.is_public ?? false}
                    onChange={(e) => updateEdit(editingId, 'is_public', e.target.checked)}
                    className="h-4 w-4 rounded border-soft/60 text-primary focus:ring-primary"
                  />
                  <span className="font-ui text-ui-sm text-ink">Public author profile</span>
                </label>

                {/* Email (read-only) + password reset */}
                <div className="rounded-md bg-soft/20 p-3">
                  <p className="font-ui text-ui-xs text-ink-light">
                    Email: <span className="font-medium text-ink">{members.find(m => m.id === editingId)?.email}</span>
                    <span className="ml-2 text-ink-light">(change via Supabase dashboard)</span>
                  </p>
                  <button
                    type="button"
                    onClick={() => sendPasswordReset(members.find(m => m.id === editingId)?.email ?? '')}
                    className="mt-2 font-ui text-ui-xs font-medium text-secondary hover:underline"
                  >
                    Send Password Reset Email
                  </button>
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button onClick={cancelEdit} className="rounded-md bg-soft/40 px-4 py-2 font-ui text-ui-sm font-medium text-ink-light hover:bg-soft/60">
                  Cancel
                </button>
                <button
                  onClick={() => saveEdit(editingId)}
                  disabled={saving === editingId}
                  className="rounded-md bg-primary px-4 py-2 font-ui text-ui-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
                >
                  {saving === editingId ? 'Saving…' : 'Save Profile'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* ── Add Member modal ── */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40" onClick={() => setShowAddModal(false)}>
          <div
            className="mx-4 w-full max-w-md rounded-xl bg-white p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="font-ui text-ui-base font-bold text-ink">Add Team Member</h2>

            <form onSubmit={handleAddMember} className="mt-4 space-y-4">
              <label className="block">
                <span className="font-ui text-ui-xs text-ink-light">Email *</span>
                <input
                  type="email" required
                  value={addForm.email}
                  onChange={(e) => setAddForm(p => ({ ...p, email: e.target.value }))}
                  className="mt-1 w-full rounded-md border border-soft/60 px-3 py-1.5 font-ui text-ui-sm text-ink"
                  placeholder="new@beyondsilence.fr"
                />
              </label>
              <label className="block">
                <span className="font-ui text-ui-xs text-ink-light">Password *</span>
                <input
                  type="password" required
                  value={addForm.password}
                  onChange={(e) => setAddForm(p => ({ ...p, password: e.target.value }))}
                  className="mt-1 w-full rounded-md border border-soft/60 px-3 py-1.5 font-ui text-ui-sm text-ink"
                  placeholder="Min. 6 characters"
                />
              </label>
              <label className="block">
                <span className="font-ui text-ui-xs text-ink-light">Display Name</span>
                <input
                  type="text"
                  value={addForm.display_name}
                  onChange={(e) => setAddForm(p => ({ ...p, display_name: e.target.value }))}
                  className="mt-1 w-full rounded-md border border-soft/60 px-3 py-1.5 font-ui text-ui-sm text-ink"
                  placeholder="Default: email username"
                />
              </label>
              <div className="grid grid-cols-2 gap-4">
                <label className="block">
                  <span className="font-ui text-ui-xs text-ink-light">Title</span>
                  <input
                    type="text"
                    value={addForm.title}
                    onChange={(e) => setAddForm(p => ({ ...p, title: e.target.value }))}
                    className="mt-1 w-full rounded-md border border-soft/60 px-3 py-1.5 font-ui text-ui-sm text-ink"
                    placeholder="e.g. Reporter"
                  />
                </label>
                <label className="block">
                  <span className="font-ui text-ui-xs text-ink-light">Role</span>
                  <select
                    value={addForm.role}
                    onChange={(e) => setAddForm(p => ({ ...p, role: e.target.value as TeamMember['role'] }))}
                    className="mt-1 w-full rounded-md border border-soft/60 bg-white px-3 py-1.5 font-ui text-ui-sm text-ink"
                  >
                    {ROLE_OPTIONS.map((r) => (<option key={r} value={r}>{r}</option>))}
                  </select>
                </label>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowAddModal(false)} className="rounded-md bg-soft/40 px-4 py-2 font-ui text-ui-sm font-medium text-ink-light hover:bg-soft/60">
                  Cancel
                </button>
                <button type="submit" disabled={adding} className="rounded-md bg-primary px-4 py-2 font-ui text-ui-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50">
                  {adding ? 'Creating…' : 'Create Member'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Container>
  );
}
